import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

export const useEditorLogic = (threadId) => {
  const { getAccessTokenSilently } = useAuth0();
  
  // Core editor states
  const editorRef = useRef(null);
  const [content, setContent] = useState('');
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentHighlight, setCurrentHighlight] = useState('#FFFF00');
  const [saveStatus, setSaveStatus] = useState('');
  const saveTimeoutRef = useRef(null);
  const [hasStartedEditing, setHasStartedEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lastSelection, setLastSelection] = useState(null);
  const selectionTimeout = useRef(null);
  
  // State for undo/redo functionality
  const [historyStack, setHistoryStack] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const MAX_HISTORY = 20; // Store up to 20 history states
  const isUndoRedoing = useRef(false);

  // Add state for context menu
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    x: 0,
    y: 0
  });

  // Cursor position tracking
  const [cursorPosition, setCursorPosition] = useState(null);
  const cursorTrackingEnabled = useRef(true);
  const isUserOperation = useRef(false);
  
  // Component mounting status
  const isMounted = useRef(true);
  
  useEffect(() => {
    // Set mounted flag
    isMounted.current = true;
    
    return () => {
      // Clear mounted flag on unmount
      isMounted.current = false;
    };
  }, []);

  // Initialize history with the initial content when it's loaded
  useEffect(() => {
    if (content && historyStack.length === 0) {
      setHistoryStack([content]);
      setHistoryIndex(0);
    }
  }, [content]);

  // Load content when threadId changes
  useEffect(() => {
    console.log('ThreadId changed, fetching data:', threadId);
    getConversationSummary();
    
    // Reset states when threadId changes
    setContent('');
    setHistoryStack([]);
    setHistoryIndex(-1);
    setHasStartedEditing(false);
  }, [threadId]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Track cursor position with MutationObserver
  useEffect(() => {
    if (!editorRef.current) return;
    
    // Setup MutationObserver to track changes to the DOM
    const observer = new MutationObserver((mutations) => {
      // After any DOM change, if there's a selection, save it
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        setLastSelection(preserveSelection());
      }
    });

    // Observe changes to the editor's content
    observer.observe(editorRef.current, {
      childList: true,
      characterData: true,
      subtree: true
    });

    // Clean up
    return () => observer.disconnect();
  }, []);

  // Basic editor functions
  const disableCursorTrackingTemporarily = () => {
    cursorTrackingEnabled.current = false;
    setTimeout(() => {
      cursorTrackingEnabled.current = true;
    }, 100);
  };

  const trackExactCursorPosition = (isUserAction = false) => {
    if (!cursorTrackingEnabled.current || !editorRef.current || !isMounted.current) return;
    
    // Only track meaningful operations
    if (isUserAction) {
      isUserOperation.current = true;
    }
    
    // Store current scroll position
    const scrollPosition = {
      top: editorRef.current.scrollTop,
      left: editorRef.current.scrollLeft
    };
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    
    // Only track if the selection is inside our editor
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;
    
    // Capture detailed node path to selection position
    const getNodePath = (node, root) => {
      const path = [];
      let current = node;
      
      while (current && current !== root) {
        const parent = current.parentNode;
        if (!parent) break;
        
        const index = Array.from(parent.childNodes).indexOf(current);
        path.unshift(index);
        current = parent;
      }
      
      return path;
    };
    
    // Store the path to restore later
    setCursorPosition({
      startPath: getNodePath(range.startContainer, editorRef.current),
      startOffset: range.startOffset,
      endPath: getNodePath(range.endContainer, editorRef.current),
      endOffset: range.endOffset,
      collapsed: range.collapsed,
      // Add scroll position to the stored cursor data
      scrollPosition: scrollPosition,
      // Add flag for user operations
      isAfterUserOperation: isUserOperation.current
    });
  };
  
  const restoreExactCursorPosition = () => {
    if (!cursorPosition || !editorRef.current || !isMounted.current) return;
    
    try {
      // Only restore if there was a previous meaningful selection or operation
      if (!cursorPosition.isAfterUserOperation) return;
      
      // Function to find a node by path
      const getNodeByPath = (path, root) => {
        let current = root;
        
        for (const index of path) {
          if (!current.childNodes || index >= current.childNodes.length) {
            return null;
          }
          current = current.childNodes[index];
        }
        
        return current;
      };
      
      // Get the nodes by their paths
      const startContainer = getNodeByPath(cursorPosition.startPath, editorRef.current);
      const endContainer = getNodeByPath(cursorPosition.endPath, editorRef.current);
      
      // If we can't find the nodes, don't attempt to restore
      if (!startContainer || !endContainer) return;
      
      // Create and set the range
      const range = document.createRange();
      range.setStart(startContainer, cursorPosition.startOffset);
      range.setEnd(endContainer, cursorPosition.endOffset);
      
      // Apply the selection without scrolling into view
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Restore scroll position using the stored values
      if (cursorPosition.scrollPosition) {
        editorRef.current.scrollTop = cursorPosition.scrollPosition.top;
        editorRef.current.scrollLeft = cursorPosition.scrollPosition.left;
        
        // Use requestAnimationFrame to ensure the scroll is set after the browser has rendered
        requestAnimationFrame(() => {
          if (editorRef.current && isMounted.current) {
            editorRef.current.scrollTop = cursorPosition.scrollPosition.top;
            editorRef.current.scrollLeft = cursorPosition.scrollPosition.left;
          }
        });
      }
    } catch (error) {
      console.error("Error restoring cursor position:", error);
    }
  };

  // Execute command with enhanced debugging
  const execCommand = (command, value = null) => {
    // Store current cursor
    trackExactCursorPosition();
    
    // Temporarily disable tracking to prevent overwriting
    disableCursorTrackingTemporarily();
    
    // Force focus
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.focus();
    }
    
    // Execute the command with debugging
    console.log(`Executing command: ${command}${value ? ' with value: ' + value : ''}`);
    const success = debugExecCommand(command, value);
    
    if (!success) {
      console.warn(`Command ${command} failed, attempting direct DOM implementation...`);
      
      // Add special handling for specific commands
      if (command === 'foreColor' && value) {
        applyColorWithDOM(value, false);
      } else if ((command === 'hiliteColor' || command === 'backColor') && value) {
        applyColorWithDOM(value, true);
      }
    }
    
    // Force sync restoration of cursor position
    setTimeout(() => {
      restoreExactCursorPosition();
    }, 10);
    
    return success;
  };

  // Debug execCommand function
  const debugExecCommand = (command, value = null) => {
    console.group(`Debug execCommand: ${command}`);
    
    try {
      console.log(`Support for ${command}: ${document.queryCommandSupported(command)}`);
      console.log(`Current state of ${command}: ${document.queryCommandState(command)}`);
      console.log(`Current value of ${command}: ${document.queryCommandValue(command)}`);
      
      const success = document.execCommand(command, false, value);
      console.log(`execCommand result: ${success ? 'succeeded' : 'failed'}`);
      
      // Log any potential restrictions
      console.log(`Editor is focused: ${document.activeElement === editorRef.current}`);
      console.log(`Selection exists: ${!!window.getSelection().rangeCount}`);
      
      return success;
    } catch (error) {
      console.error(`Error executing ${command}:`, error);
      return false;
    } finally {
      console.groupEnd();
    }
  };

  // Apply color with direct DOM manipulation
  const applyColorWithDOM = (color, isBackground = false) => {
    console.log(`Applying ${isBackground ? 'background' : 'text'} color with direct DOM:`, color);
    
    // Ensure the editor is focused
    if (!editorRef.current) {
      console.error('Editor reference not available');
      return false;
    }
    
    if (document.activeElement !== editorRef.current) {
      console.log('Editor not focused, forcing focus');
      editorRef.current.focus();
    }
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      console.error('No selection available');
      return false;
    }
    
    try {
      // Create a span with the style
      const span = document.createElement('span');
      if (isBackground) {
        span.style.backgroundColor = color;
      } else {
        span.style.color = color;
      }
      
      const range = selection.getRangeAt(0);
      
      // If the selection is collapsed (just a cursor)
      if (selection.isCollapsed) {
        console.log('Selection is just a cursor, inserting colored placeholder');
        span.textContent = '\u200B'; // Zero-width space
        range.insertNode(span);
        
        // Position cursor after span
        range.setStartAfter(span);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      // If text is selected
      else {
        console.log('Wrapping selected text with colored span');
        const fragment = range.extractContents();
        span.appendChild(fragment);
        range.insertNode(span);
        
        // Reselect the newly wrapped content
        range.selectNodeContents(span);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      return true;
    } catch (error) {
      console.error('DOM manipulation failed:', error);
      return false;
    }
  };

  // Undo/Redo functions
  const handleUndo = () => {
    if (historyIndex > 0) {
      isUndoRedoing.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      
      if (editorRef.current) {
        editorRef.current.innerHTML = historyStack[newIndex];
        setContent(historyStack[newIndex]);
      }
      
      setTimeout(() => {
        isUndoRedoing.current = false;
      }, 0);
    }
  };

  const handleRedo = () => {
    if (historyIndex < historyStack.length - 1) {
      isUndoRedoing.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      
      if (editorRef.current) {
        editorRef.current.innerHTML = historyStack[newIndex];
        setContent(historyStack[newIndex]);
      }
      
      setTimeout(() => {
        isUndoRedoing.current = false;
      }, 0);
    }
  };

  // Save state to history stack
  const saveToHistory = (newContent) => {
    if (isUndoRedoing.current) return;
    
    // Only save if content actually changed
    if (historyStack[historyIndex] === newContent) return;
    
    const newStack = historyStack.slice(0, historyIndex + 1);
    newStack.push(newContent);
    
    // Trim history if it exceeds max size
    if (newStack.length > MAX_HISTORY) {
      newStack.shift();
    }
    
    setHistoryStack(newStack);
    setHistoryIndex(newStack.length - 1);
  };

  // Toggle format function
  const toggleFormat = (command) => {
    const activeSelection = window.getSelection();
    const hasSelection = activeSelection && !activeSelection.isCollapsed;
    
    if (hasSelection) {
      // Track current cursor position
      trackExactCursorPosition(true); // Mark as user operation
      
      // Disable tracking temporarily
      disableCursorTrackingTemporarily();
      
      // Check if the selected text already has the formatting
      let hasFormat = false;
      
      if (command === 'bold') {
        hasFormat = document.queryCommandState('bold');
      } else if (command === 'italic') {
        hasFormat = document.queryCommandState('italic');
      } else if (command === 'underline') {
        hasFormat = document.queryCommandState('underline');
      } else if (command === 'strikeThrough') {
        hasFormat = document.queryCommandState('strikeThrough');
      }
      
      // Handle format toggle
      if (hasFormat) {
        // Get current format states before removing format
        const isBold = document.queryCommandState('bold');
        const isItalic = document.queryCommandState('italic');
        const isUnderline = document.queryCommandState('underline');
        const isStrikeThrough = document.queryCommandState('strikeThrough');
        
        // Remove formatting
        document.execCommand('removeFormat', false, null);
        
        // Reapply other formats that were active
        if (command !== 'bold' && isBold) document.execCommand('bold', false, null);
        if (command !== 'italic' && isItalic) document.execCommand('italic', false, null);
        if (command !== 'underline' && isUnderline) document.execCommand('underline', false, null);
        if (command !== 'strikeThrough' && isStrikeThrough) document.execCommand('strikeThrough', false, null);
      } else {
        // If the text doesn't have the format, apply it
        document.execCommand(command, false, null);
      }
      
      // Reset user operation flag after a short delay
      setTimeout(() => {
        isUserOperation.current = false;
      }, 100);
      
      // Force restore cursor position
      setTimeout(() => {
        restoreExactCursorPosition();
      }, 10);
    }
  };

  // Process content changes
  const handleInput = (e) => {
    if (!isMounted.current) return;
    
    const newContent = e.target.innerHTML;
    setContent(newContent);
    
    // Store current selection
    const currentSelection = preserveSelection();

    // Only start auto-saving after the first edit
    if (!hasStartedEditing) {
      setHasStartedEditing(true);
    }

    // Save content to history (for undo/redo)
    saveToHistory(newContent);

    if (hasStartedEditing) {
      debouncedSave(newContent);
    }
    
    // Retain selection
    if (currentSelection) {
      setLastSelection(currentSelection);
    }
  };

  // Debounced save function
  const debouncedSave = useCallback((newContent) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    const timeoutId = setTimeout(async () => {
      try {
        if (!isMounted.current || !threadId) {
          console.log('Save aborted - component unmounted or no threadId');
          return;
        }
        
        setSaveStatus('Saving...');
        const token = await getAccessTokenSilently();
        
        console.log('Saving content for threadId:', threadId);
        
        await axios.post(`${process.env.REACT_APP_SERVER}saveConversationSummary`, {
          summary: newContent,
          threadId: threadId
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Only update UI if still mounted
        if (isMounted.current) {
          setSaveStatus('Saved');
          console.log('Content saved successfully');

          // Clear save status after 2 seconds
          setTimeout(() => {
            if (isMounted.current) {
              setSaveStatus('');
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error saving document:', error);
        
        // Only update UI if still mounted
        if (isMounted.current) {
          setSaveStatus('Error saving');
          
          // Clear error status after 5 seconds
          setTimeout(() => {
            if (isMounted.current) {
              setSaveStatus('');
            }
          }, 5000);
        }
      }
    }, 1000);

    saveTimeoutRef.current = timeoutId;
  }, [threadId, getAccessTokenSilently, isMounted]);

  // Preserve current selection
  const preserveSelection = () => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
      return {
        range: selection.getRangeAt(0).cloneRange(),
        isCollapsed: selection.isCollapsed
      };
    }
    return null;
  };

  // Restore saved selection
  const restoreSelection = (selectionState) => {
    if (!selectionState || selectionState.isCollapsed) return;
    
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(selectionState.range);
  };

  // Get conversation summary from API
  const getConversationSummary = async (refresh = false) => {
    try {
      setIsLoading(true);
      setContent('');
      
      if (!threadId) {
        console.error('No threadId provided to TextEditor');
        setIsLoading(false);
        return;
      }
      
      console.log('Fetching summary for threadId:', threadId);
      
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${process.env.REACT_APP_SERVER}conversationSummary/${threadId}?refresh=${refresh}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Summary fetched successfully');

      setContent(response.data.summary);
    } catch (error) {
      console.error('Error fetching conversation summary:', error);
      
      // Set a simple placeholder when summary fails to load
      if (editorRef.current) {
        setContent('<p>Could not load summary. Click "Recreate" to try again.</p>');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle font size changes
  const handleFontSize = (size) => {
    const activeSelection = window.getSelection();
    
    // Store the original range
    const originalRange = activeSelection.rangeCount > 0 ? 
      activeSelection.getRangeAt(0).cloneRange() : null;
    
    // If no selection or no range, just return
    if (!originalRange) return;
    
    // Set this as a user operation
    isUserOperation.current = true;
    
    // Check if we have actual text selected (not collapsed)
    if (!activeSelection.isCollapsed) {
      // Store the selected content
      const content = originalRange.extractContents();
      
      // Create a span with the desired font size
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.appendChild(content);
      
      // Insert the span at the cursor position
      originalRange.insertNode(span);
      
      // Create a new range that selects the content inside the span
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      
      // Apply the new selection
      activeSelection.removeAllRanges();
      activeSelection.addRange(newRange);
    } else {
      // If no text is selected, place a span at the cursor position
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.innerHTML = '&#8203;'; // Zero-width space to allow cursor to be inside span
      
      originalRange.insertNode(span);
      
      // Position cursor inside the span
      const newRange = document.createRange();
      newRange.setStart(span.firstChild, 1);
      newRange.setEnd(span.firstChild, 1);
      
      activeSelection.removeAllRanges();
      activeSelection.addRange(newRange);
    }
    
    // Track this position with user operation flag
    trackExactCursorPosition(true);
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isUserOperation.current = false;
    }, 100);
  };

  // Handle context menu actions
  const handleContextMenuAction = (action) => {
    // Restore selection if it exists
    if (lastSelection) {
      restoreSelection(lastSelection);
    }
    
    // Store the original selection before modifying
    const originalSelection = preserveSelection();
    
    // Execute the action
    switch (action) {
      case 'cut':
        document.execCommand('cut');
        break;
      case 'copy':
        document.execCommand('copy');
        break;
      case 'paste':
        document.execCommand('paste');
        break;
      case 'delete':
        document.execCommand('delete');
        break;
      case 'selectAll':
        document.execCommand('selectAll');
        break;
      case 'bold':
        toggleFormat('bold');
        return; // toggleFormat already handles selection preservation
      case 'italic':
        toggleFormat('italic');
        return; // toggleFormat already handles selection preservation
      case 'underline':
        toggleFormat('underline');
        return; // toggleFormat already handles selection preservation
      default:
        break;
    }
    
    // Collect the updated content
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
      saveToHistory(newContent);
    }
    
    // Close the context menu
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
    
    // Focus back on editor
    if (editorRef.current) {
      editorRef.current.focus();
      
      // For operations that don't delete the selection, try to restore it
      if (action === 'copy' && originalSelection) {
        setTimeout(() => {
          try {
            restoreSelection(originalSelection);
          } catch (e) {
            console.log("Could not restore selection after context menu action", e);
          }
        }, 10);
      } else {
        // For other operations, capture the new selection
        setTimeout(() => {
          const newSelection = preserveSelection();
          if (newSelection) {
            setLastSelection(newSelection);
          }
        }, 10);
      }
    }
  };

  // Color handling functions
  const handleColorClick = (color) => {
    console.log('Color clicked:', color);
    setCurrentColor(color);
    
    // Ensure there's a selection or focus before proceeding
    const selection = window.getSelection();
    
    // Force editor focus
    if (editorRef.current) {
      editorRef.current.focus();
      
      // Wait a bit for the focus to take effect
      setTimeout(() => {
        // First try with debug command
        const success = debugExecCommand('foreColor', color);
        
        // If that fails, try direct DOM manipulation
        if (!success) {
          console.log('execCommand failed, trying direct DOM manipulation');
          applyColorWithDOM(color, false);
        }
      }, 50);
    } else {
      console.error('Editor reference not available');
    }
  };

  const handleHighlightClick = (color) => {
    console.log('Highlight color clicked:', color);
    setCurrentHighlight(color);
    
    // Ensure there's a selection before proceeding
    const selection = window.getSelection();
    
    // Force editor focus
    if (editorRef.current) {
      editorRef.current.focus();
      
      // Wait a bit for the focus to take effect
      setTimeout(() => {
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
          // Try hiliteColor with debugging first
          let success = false;
          
          if (document.queryCommandSupported('hiliteColor')) {
            success = debugExecCommand('hiliteColor', color);
          }
          
          // If hiliteColor fails, try backColor
          if (!success && document.queryCommandSupported('backColor')) {
            success = debugExecCommand('backColor', color);
          }
          
          // If both standard approaches fail, use DOM manipulation
          if (!success) {
            console.log('Standard highlight commands failed, trying direct DOM manipulation');
            applyColorWithDOM(color, true);
          }
        } else {
          console.warn('No selection or collapsed selection for highlight');
        }
      }, 50);
    } else {
      console.error('Editor reference not available');
    }
  };

  // Handle mouse events
  const handleMouseUp = (e) => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      // Store selection for future restoration
      setLastSelection(preserveSelection());
      
      // Keep editor focused
      editorRef.current.focus();
      
      // Clear any existing timeout
      if (selectionTimeout.current) {
        clearTimeout(selectionTimeout.current);
      }
    }
  };

  // Handle image clicks
  const handleImageClick = (e) => {
    if (e.target.tagName === 'IMG') {
      const prevSelected = document.querySelector('img.selected');
      if (prevSelected) {
        prevSelected.classList.remove('selected');
      }

      e.target.classList.add('selected');
      setSelectedImage(e.target);
      e.preventDefault();
    } else {
      const prevSelected = document.querySelector('img.selected');
      if (prevSelected) {
        prevSelected.classList.remove('selected');
      }
      setSelectedImage(null);
    }
  };

  // Link handling
  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  // Handle context menu opening
  const handleContextMenu = (e) => {
    // Only show custom context menu when text is selected
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      e.preventDefault();
      setContextMenu({
        isOpen: true,
        x: e.clientX,
        y: e.clientY
      });
      
      // Save the current selection
      setLastSelection(preserveSelection());
    }
  };

  return {
    // State
    editorRef,
    content,
    currentColor,
    currentHighlight,
    saveStatus,
    hasStartedEditing,
    isLoading,
    selectedImage,
    historyStack,
    historyIndex,
    lastSelection,
    contextMenu,
    cursorPosition,
    
    // Functions
    setContent,
    setCurrentColor,
    setCurrentHighlight,
    setSelectedImage,
    setSaveStatus,
    setContextMenu,
    setLastSelection,
    
    // Core editor functionality
    execCommand,
    handleInput,
    handleUndo,
    handleRedo,
    toggleFormat,
    handleFontSize,
    handleColorClick,
    handleHighlightClick,
    handleLink,
    handleMouseUp,
    handleImageClick,
    handleContextMenu,
    handleContextMenuAction,
    preserveSelection,
    restoreSelection,
    saveToHistory,
    trackExactCursorPosition,
    restoreExactCursorPosition,
    
    // API interactions
    getConversationSummary,
    debouncedSave
  };
}; 